import Geocoder from './Geocoder';
import debounce from 'lodash/debounce';

const FORM_SELECTOR = '[data-geolocation-form]';

const ADDRESS_SELECTOR = '[data-geolocation="address"]';
const LAT_SELECTOR = '[data-geolocation="lat"]';
const LNG_SELECTOR = '[data-geolocation="lng"]';

const ERROR_NO_PLACE_RETURNED = 1;
const ERROR_INVALID_PLACE_RETURNED = 2;

function logErrorMessage(err) {
  let msg;

  switch (err) {
    case ERROR_NO_PLACE_RETURNED:
      msg = 'No place found for provided user input';
      break;

    case ERROR_INVALID_PLACE_RETURNED:
      msg = 'Returned place is invalid, missing geometry';
      break;

    default:
      throw new Error(`Invalid error message ${err}`);
  }

  return msg;
}

const debouncedGetPlacePredictions = debounce(function(
  service,
  options,
  callback
) {
  service.getPlacePredictions(options, callback);
},
100);

const debouncedGetPlaceDetails = debounce(function(service, options, callback) {
  service.getDetails(options, callback);
}, 100);

const autocompleteOptions = {
  componentRestrictions: {
    country: 'au'
  }
};

class GeolocationForm {
  constructor(form) {
    this.searchForm = form;
    this.addressField = form.querySelector(ADDRESS_SELECTOR);

    if (!this.addressField) {
      throw new Error(`Missing element ${ADDRESS_SELECTOR}`);
    }
    this.latField = form.querySelector(LAT_SELECTOR);

    if (!this.latField) {
      throw new Error(`Missing element ${LAT_SELECTOR}`);
    }

    this.lngField = form.querySelector(LNG_SELECTOR);
    if (!this.lngField) {
      throw new Error(`Missing element ${LNG_SELECTOR}`);
    }

    this.initialize();
    this.attachEventHandlers();
  }

  initialize() {


    this.autocomplete = new google.maps.places.Autocomplete(
      this.addressField,
      autocompleteOptions
    );

    if (!this.addressField.value) {
      new Geocoder().getCurrentAddress().then(currentAddress => {
        this.updateAddressField(currentAddress);
      });
    }
  }

  updateAddressField(currentAddress) {
    this.addressField.value = currentAddress;
  }

  resetInputOnInvalidPlace(err) {
    if (
      err === ERROR_NO_PLACE_RETURNED ||
      err === ERROR_INVALID_PLACE_RETURNED
    ) {
      this.addressField.value = '';
      this.updateCoordinates(null);
    }
    logErrorMessage(err);
  }

  attachEventHandlers() {
    this.autocomplete.addListener(
      'place_changed',
      this.onPlaceChanged.bind(this)
    );

    this.addressField.addEventListener('blur', this.onPlaceChanged.bind(this));

    /* Make sure autocomplete is finished before submitting the form */
    this.searchForm.addEventListener('submit', event => {
      event.preventDefault();
      this.onPlaceChanged().then(() => this.searchForm.submit());
    });
  }

  validatePlace(place) {
    /* Invalid place is a stub { name: value of input } and no other properites */
    return !!place.geometry;
  }

  updateAddressInputWithFirstAutocompleteResult() {
    return new Promise((resolve, reject) => {
      let req = {
        input: this.addressField.value,
        componentRestrictions: {
          country: 'au'
        }
      };

      this.getPlacePredictions(req, autocompletePrediction => {
        if (!autocompletePrediction) {
          reject(ERROR_NO_PLACE_RETURNED);
          return;
        }
        let place = autocompletePrediction.shift();

        this.getPlaceDetails(place.place_id, result => {
          if (this.validatePlace(result)) {
            this.addressField.value = place.description;
            this.updateCoordinates(result);
            resolve(place);
            return;
          }
          reject(ERROR_INVALID_PLACE_RETURNED);
        });
      });
    });
  }

  onPlaceChanged() {
    return new Promise(resolve => {
      let place = this.autocomplete.getPlace();

      if (this.addressField.value.trim() === '') {
        place = null;
        this.updateCoordinates(place);
        resolve(place);
        return;
      }

      if (!place || this.validatePlace(place) === false) {
        this.updateAddressInputWithFirstAutocompleteResult()
          .then(place => {
            return resolve(place);
          })
          .catch(err => {
            this.resetInputOnInvalidPlace(err);
          });
        return;
      }

      this.updateCoordinates(place);
      resolve(place);
    });
  }

  getLocation(place) {
    if (!place || !place.geometry) {
      return { lat: null, lng: null };
    }

    return {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    };
  }

  updateCoordinates(place) {
    this.latField.value = this.getLocation(place).lat;
    this.lngField.value = this.getLocation(place).lng;
  }

  getPlacePredictions(request, callback) {
    if (!this.autocompleteService) {
      this.autocompleteService = new google.maps.places.AutocompleteService();
    }

    debouncedGetPlacePredictions(this.autocompleteService, request, callback);
  }

  getPlaceDetails(placeId, callback) {
    if (!this.placesService) {
      this.placesService = new google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
    debouncedGetPlaceDetails(
      this.placesService,
      { placeId: placeId },
      callback
    );
  }
}

function importGeolocationForms() {
  Array.prototype.forEach.call(
    document.querySelectorAll(FORM_SELECTOR),
    form => {
      new GeolocationForm(form);
    }
  );
}

export default importGeolocationForms;



// WEBPACK FOOTER //
// ./src/javascripts/Geolocation/Geolocation.js